/**
 * Public Signal Draft Generator V1 - Closeout Sanitizer
 * 
 * Sanitizes closeout artifacts by removing internal file paths,
 * module names, hashes, receipt structures, and other sensitive details.
 * 
 * BOUNDARIES:
 * - Advisory only: no execution authority
 * - Deterministic output only
 * - Fail closed on forbidden exposure patterns
 * - No mutation of inputs
 */

import {
  CloseoutArtifact,
  RemovedDetail,
  SanitizationResult,
} from './types';
import { scanForbiddenTerms } from './forbiddenTermScanner';

/**
 * Sanitize a string by removing forbidden patterns
 */
function sanitizeString(
  text: string,
  fieldName: string
): SanitizationResult {
  const removedDetails: RemovedDetail[] = [];
  let sanitizedText = text;

  // Scan for forbidden patterns
  const matches = scanForbiddenTerms(text);

  for (const match of matches) {
    removedDetails.push({
      category: match.category,
      original: match.matchedText,
      reason: `Removed ${match.category} from ${fieldName}`,
    });

    // Replace with placeholder based on category
    switch (match.category) {
      case 'path':
        sanitizedText = sanitizedText.replace(match.matchedText, '[internal path]');
        break;
      case 'module':
        sanitizedText = sanitizedText.replace(match.matchedText, '[module]');
        break;
      case 'hash':
        sanitizedText = sanitizedText.replace(match.matchedText, '[hash]');
        break;
      case 'receipt':
        sanitizedText = sanitizedText.replace(match.matchedText, '[record]');
        break;
      case 'governance':
        sanitizedText = sanitizedText.replace(match.matchedText, '[internal system]');
        break;
      case 'ip_sensitive':
        sanitizedText = sanitizedText.replace(match.matchedText, '[proprietary]');
        break;
      case 'repo_name':
        sanitizedText = sanitizedText.replace(match.matchedText, '[repository]');
        break;
    }
  }

  return {
    sanitized: matches.length > 0,
    removedDetails,
    remainingText: sanitizedText,
  };
}

/**
 * Sanitize closeout artifact
 */
export function sanitizeCloseoutArtifact(
  artifact: CloseoutArtifact
): { sanitizedArtifact: CloseoutArtifact; removedDetails: RemovedDetail[] } {
  // Create a deep copy to avoid mutation
  const sanitizedArtifact: CloseoutArtifact = JSON.parse(JSON.stringify(artifact));
  const allRemovedDetails: RemovedDetail[] = [];

  // Sanitize module field
  if (sanitizedArtifact.module) {
    const moduleResult = sanitizeString(sanitizedArtifact.module, 'module');
    sanitizedArtifact.module = moduleResult.remainingText;
    allRemovedDetails.push(...moduleResult.removedDetails);
  }

  // Sanitize issue field
  if (sanitizedArtifact.issue) {
    const issueResult = sanitizeString(sanitizedArtifact.issue, 'issue');
    sanitizedArtifact.issue = issueResult.remainingText;
    allRemovedDetails.push(...issueResult.removedDetails);
  }

  // Sanitize change descriptions
  if (sanitizedArtifact.changes) {
    for (const change of sanitizedArtifact.changes) {
      if (change.description) {
        const descResult = sanitizeString(change.description, 'change.description');
        change.description = descResult.remainingText;
        allRemovedDetails.push(...descResult.removedDetails);
      }
    }
  }

  // Sanitize invariants
  if (sanitizedArtifact.invariantsPreserved) {
    for (let i = 0; i < sanitizedArtifact.invariantsPreserved.length; i++) {
      const invariant = sanitizedArtifact.invariantsPreserved[i];
      const invariantResult = sanitizeString(invariant, 'invariant');
      sanitizedArtifact.invariantsPreserved[i] = invariantResult.remainingText;
      allRemovedDetails.push(...invariantResult.removedDetails);
    }
  }

  // Remove all keys that might contain sensitive data
  const sensitiveKeys = [
    'bundleScan',
    'incidentSummary',
    'dependencies',
    'integrationPoints',
    'forbiddenPatterns',
    'forbiddenPaths',
    'forbiddenSelfImprovement',
    'sanitizationRules',
    'validationRules',
  ];

  for (const key of sensitiveKeys) {
    if (key in sanitizedArtifact) {
      allRemovedDetails.push({
        category: 'ip_sensitive',
        original: JSON.stringify(sanitizedArtifact[key]),
        reason: `Removed sensitive field: ${key}`,
      });
      delete sanitizedArtifact[key];
    }
  }

  return {
    sanitizedArtifact,
    removedDetails: allRemovedDetails,
  };
}

/**
 * Sanitize docs summary
 */
export function sanitizeDocsSummary(
  docsSummary: string
): SanitizationResult {
  return sanitizeString(docsSummary, 'docsSummary');
}
